'use client';

import { useState, useMemo, useCallback, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

import type { ReactNode } from 'react';

// Toast 信息接口
interface IToast {
    id: string;
    message: string;
}

// Toast 组件接口
interface IToastComponent {
    id: string;
    element: ReactNode;
}

// Toast 上下文接口
interface IToastContextType {
    // 添加普通文本 Toast
    addToast: (message: string) => void;
    // 添加自定义组件 Toast
    addToastComponent: (node: ReactNode) => void;
}

// toast 提示持续时间
export const HINT_DURATION_MS = 2000;

// 生成随机 id 时用到的进制数
const RANDOM_RADIX = 36;

export const ToastContext = createContext<IToastContextType>({
    addToast: () => {},
    addToastComponent: () => {},
});

/** Non-React call sites（如纯 async）可调用；挂载前或未提供 Provider 时为 no-op */
let globalAddToast: ((message: string, duration?: number) => void) | null = null;

export function showToast(message: string, duration?: number): void {
    globalAddToast?.(message, duration);
}

export function useToast(): IToastContextType {
    return useContext(ToastContext);
}

/**
 * toast 通用组件
 * 提供者组件，负责管理和展示 toast 信息
 * 结合 useToast 自定义 Hook 使用
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    // 存储当前所有的 Toast 信息
    const [toasts, setToasts] = useState<(IToast | IToastComponent)[]>([]);

    // 初始化状态，用来解决服务端渲染的问题
    const [mounted, setMounted] = useState(false);

    // 添加普通文本 Toast
    const addToast = useCallback((message: string, duration = HINT_DURATION_MS) => {
        // 生成唯一 id
        const id = Math.random().toString(RANDOM_RADIX);
        // 添加 Toast 信息
        setToasts(prev => [...prev, { id, message }]);

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    // 添加自定义组件 Toast
    const addToastComponent = useCallback((element: ReactNode, duration = HINT_DURATION_MS) => {
        const id = Math.random().toString(RANDOM_RADIX);
        setToasts(prev => [...prev, { id, element }]);

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    // 上下文 context，供provider消费
    const contextValue = useMemo(() => ({ addToast, addToastComponent }), [addToast, addToastComponent]);

    // 初始化
    useEffect(() => {
        // 初始化状态，用来解决服务端渲染的问题
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) {
            return undefined;
        }
        globalAddToast = addToast;
        return () => {
            globalAddToast = null;
        };
    }, [mounted, addToast]);

    // 服务端渲染时返回 null，避免错误
    if (!mounted) {
        return null;
    }

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {/* toast 自动消失 */}
            {toasts.length > 0 &&
                createPortal(
                    <div className='fixed left-1/2 top-1/2 z-50 flex w-[320px] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[8px]'>
                        {toasts.map(toast => (
                            <div
                                key={toast.id}
                                className='font-size-[14px] font-weight-[500] mt-[10px] whitespace-pre-line rounded-[12px] bg-[#454545] p-[16px] text-center leading-[20px] text-white'
                            >
                                {(toast as IToast).message
                                    ? (toast as IToast).message
                                    : (toast as IToastComponent).element}
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
        </ToastContext.Provider>
    );
}
